import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { getAuth, onAuthStateChanged } from "@firebase/auth";
import { authenticateUser } from "../api";

type Auth = {
  authToken: string;
  authUser?: any;
  firebaseUid?: string | null;
  authenticate(): Promise<void>;
  refreshEntitlement(): Promise<void>;
  logout(): void;
  hasActivePayment: boolean;
};

export const AuthContext = createContext<Auth>({
  authToken: "",
  authUser: undefined,
  firebaseUid: null,
  logout: () => {},
  authenticate: () => Promise.resolve(),
  refreshEntitlement: () => Promise.resolve(),
  hasActivePayment: false,
});

/* Firebase collapses every transport-level problem into one code, including a
   fetch the browser cancelled because the page navigated. None of these mean the
   credentials are bad, so none of them should end the session. */
const isNetworkAuthError = (err: any): boolean =>
  err?.code === "auth/network-request-failed" ||
  err?.code === "auth/timeout" ||
  /network|load failed|fetch/i.test(String(err?.message ?? ""));

export const AuthProvider: any = ({ children }: any) => {
  const [authToken, setAuthToken] = useState<any>(
    localStorage.getItem("little-feather-token")
  );
  const [authUser, setAuthUser] = useState<any | null>(false);
  const [firebaseUid, setFirebaseUid] = useState<string | null>(null);
  const [hasActivePayment, setHasActivePayment] = useState(false);
  const signedInRef = useRef(false);

  const logout = useCallback(async (reload = true) => {
    const auth = getAuth();
    await auth.signOut();
    localStorage.removeItem("little-feather-token");
    localStorage.removeItem("selected-baby");
    signedInRef.current = false;
    setAuthToken(null);
    setAuthUser(null);
    setFirebaseUid(null);
    setHasActivePayment(false);
    if (reload) {
      window.location.reload();
    }
  }, []);

  /* Re-reads entitlement on demand. Deliberately unguarded: a native purchase
     does not reload the page, so this is the only way the paywall can clear
     without waiting for the next poll. */
  const refreshEntitlement = useCallback(async () => {
    if (!signedInRef.current) return;
    try {
      const userData = await authenticateUser();
      if (userData?.data) {
        setAuthUser(userData.data);
        setHasActivePayment(!!userData.data.activePayment);
      }
    } catch (err) {
      /* Leave the last known state alone rather than dropping the user to the
         paywall because a single request failed. */
      console.error("Failed to refresh entitlement:", err);
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();

    /* One subscription for the lifetime of the provider. The previous code
       registered a fresh listener on every poll and never unsubscribed, which
       accumulated roughly thirty live listeners an hour, each one triggering a
       token refresh and an API call. */
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (!user) {
          if (localStorage.getItem("little-feather-token")) {
            await logout();
          } else {
            signedInRef.current = false;
            setAuthUser(null);
            setFirebaseUid(null);
            setHasActivePayment(false);
          }
          return;
        }

        if (!user.emailVerified) {
          await logout(false);
          return;
        }

        /* Prefer a forced refresh, but fall back to the cached token when the
           network round-trip fails. Inside the native WebView this call fails
           intermittently — Firebase reports every transport failure, including a
           request cancelled by navigation, as auth/network-request-failed — and a
           cached token is still perfectly valid for up to an hour. */
        let newToken: string;
        try {
          newToken = await user.getIdToken(true);
        } catch (refreshErr) {
          if (!isNetworkAuthError(refreshErr)) throw refreshErr;
          console.warn("Token refresh failed; using cached token.", refreshErr);
          newToken = await user.getIdToken(false);
        }

        localStorage.setItem("little-feather-token", newToken);
        signedInRef.current = true;
        setAuthToken(newToken);
        setFirebaseUid(user.uid);

        const userData = await authenticateUser();
        if (userData?.data) {
          setAuthUser(userData.data);
          setHasActivePayment(!!userData.data.activePayment);
        }
      } catch (err) {
        /* Never sign a user out because the network blipped. Previously this
           whole block sat outside the async callback, so errors in here could not
           reach it at all and were silently dropped; moving it inside made real
           errors visible but also turned a transient failure into a forced
           logout, which is worse than the bug it fixed. Only credential problems
           justify destroying the session — anything transient is left alone for
           the periodic refresh to retry. */
        if (isNetworkAuthError(err)) {
          console.warn(
            "Auth check failed on a network error; keeping session.",
            err
          );
          return;
        }
        console.error("Error during authentication:", err);
        await logout();
      }
    });

    return unsubscribe;
  }, [logout]);

  /* Periodic re-check only refreshes entitlement; it no longer re-runs the whole
     auth flow. Renewals, refunds and expiry all happen server-side, so the client
     has to ask rather than assume. */
  useEffect(() => {
    const intervalId = setInterval(refreshEntitlement, 120000);
    return () => clearInterval(intervalId);
  }, [refreshEntitlement]);

  return (
    <AuthContext.Provider
      value={{
        authToken,
        authUser,
        firebaseUid,
        authenticate: refreshEntitlement,
        refreshEntitlement,
        logout,
        hasActivePayment,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
